import { useState, useEffect } from 'react';
import { Loader2, Upload, X, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { db, storage } from '@/firebase/config';
import { collection, addDoc, doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

import { Resource } from './IndependentResourceComponents';

interface PersonalResource extends Resource {
  storagePath?: string;
}

interface PersonalBookUploadFormProps {
  userId: string;
  // Course id (under teaching_workspaces/{uid}/courses/{subjectId}).
  // When provided, the form auto-fills the Subject field by reading the course doc's `title` field.
  subjectId?: string;
  // Optional fallback name passed via navigation state, used only if the Firebase fetch fails.
  subjectNameFallback?: string;
  // Firestore collection path (defaults to the personal library).
  // If you pass a course-resources path, the file is written directly there
  // (no mirror step needed in the caller).
  collectionPath?: string;
  // Storage prefix (defaults to the personal library). Keep these two in sync.
  storagePrefix?: string;
  // Button label + toast text customization (so the same form can be reused
  // for both the personal library and per-course resources).
  submitLabel?: string;
  successMessage?: string;
  onClose?: () => void;
  onSubmit?: (data: PersonalResource) => Promise<void>;
}

export function PersonalBookUploadForm({
  userId,
  subjectId,
  subjectNameFallback,
  collectionPath,
  storagePrefix,
  submitLabel = 'Upload to My Library',
  successMessage = 'Material added to your library',
  onClose,
  onSubmit,
}: PersonalBookUploadFormProps) {
  const resolvedCollectionPath = collectionPath || `teaching_workspaces/${userId}/personal_resources`;
  const resolvedStoragePrefix = storagePrefix || `teaching_workspaces/${userId}/personal_resources/`;
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    type: 'book',
    description: '',
    year: new Date().getFullYear().toString(),
    tags: [] as string[],
  });
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const { toast } = useToast();

  // Auto-fill subject + title from the course doc (teaching_workspaces/{uid}/courses/{subjectId}.title)
  useEffect(() => {
    let cancelled = false;
    const loadSubject = async () => {
      let resolvedName = subjectNameFallback || '';
      if (subjectId) {
        setSubjectLoading(true);
        try {
          const snap = await getDoc(doc(db, 'teaching_workspaces', userId, 'courses', subjectId));
          if (!cancelled && snap.exists()) {
            const data = snap.data() as { title?: string };
            if (data.title) resolvedName = data.title;
          }
        } catch (err) {
          console.warn('Failed to load course for subject auto-fill:', err);
        } finally {
          if (!cancelled) setSubjectLoading(false);
        }
      }
      if (cancelled) return;
      setFormData((prev) => ({
        ...prev,
        subject: resolvedName,
        title: prev.title || (resolvedName ? `${resolvedName} Resource` : ''),
      }));
    };
    loadSubject();
    return () => { cancelled = true; };
  }, [userId, subjectId, subjectNameFallback]);

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const trimmed = tagInput.trim();
      if (!formData.tags.includes(trimmed)) {
        setFormData({ ...formData, tags: [...formData.tags, trimmed] });
      }
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({ ...formData, tags: formData.tags.filter((t) => t !== tag) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    try {
      const safeName = file.name.replace(/\s+/g, '_');
      const storagePath = `${resolvedStoragePrefix}${Date.now()}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const resourceData: PersonalResource = {
        id: '',
        ...formData,
        description: formData.description || '',
        isBookmarked: false,
        downloadCount: 0,
        fileType: file.type.split('/')[1]?.toUpperCase() || 'FILE',
        fileSize: (file.size / 1024 / 1024).toFixed(2) + ' MB',
        uploadDate: new Date().toISOString(),
        pdfUrl: url,
        storagePath: storagePath,
        grade: '',
        examType: '',
      };

      const docRef = await addDoc(collection(db, resolvedCollectionPath), resourceData);
      resourceData.id = docRef.id;

      onSubmit?.(resourceData);
      onClose?.();
      toast({ title: 'Success', description: successMessage });
    } catch (err) {
      console.error(err);
      toast({ title: 'Error', description: 'Failed to upload material', variant: 'destructive' });
      setUploading(false);
    }
  };

  return (
    <ScrollArea className="h-[70vh] pr-4">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Subject</Label>
            <div className="flex items-center gap-2">
              <Input
                value={subjectLoading ? 'Loading subject…' : formData.subject}
                readOnly
                className="bg-slate-100 dark:bg-slate-900/60 border-slate-200 dark:border-slate-800 rounded-lg text-sm font-bold text-slate-700 dark:text-slate-200 cursor-not-allowed"
              />
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/30 rounded-full px-2 py-1">
                <BookOpen className="h-3 w-3" /> Auto
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="Resource Title"
              className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Resource Type</Label>
            <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
              <SelectTrigger className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100">
                <SelectValue placeholder="Select Type" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                <SelectItem value="book" className="text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 focus:bg-slate-100 dark:focus:bg-slate-800/60 cursor-pointer">Book</SelectItem>
                <SelectItem value="paper" className="text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 focus:bg-slate-100 dark:focus:bg-slate-800/60 cursor-pointer">Paper</SelectItem>
                <SelectItem value="guide" className="text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 focus:bg-slate-100 dark:focus:bg-slate-800/60 cursor-pointer">Study Guide</SelectItem>
                <SelectItem value="notes" className="text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 focus:bg-slate-100 dark:focus:bg-slate-800/60 cursor-pointer">Notes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Year</Label>
            <Input
              type="number"
              value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: e.target.value })}
              className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Description <span className="text-[10px] text-slate-400 dark:text-slate-500 normal-case font-medium">(optional)</span>
          </Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of the resource..."
            className="min-h-[100px] bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Tags</Label>
          {formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {formData.tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="cursor-pointer bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-500/30"
                  onClick={() => removeTag(tag)}
                >
                  {tag} <X className="h-3 w-3 ml-1" />
                </Badge>
              ))}
            </div>
          )}
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleAddTag}
            placeholder="Type a tag and press Enter"
            className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">File</Label>
          <Input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            required
            className="cursor-pointer bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm text-slate-700 dark:text-slate-300 file:bg-indigo-50 file:dark:bg-indigo-500/20 file:text-indigo-700 file:dark:text-indigo-300 file:border-0 file:rounded-md file:px-3 file:py-1 file:mr-3 file:font-bold file:text-xs file:cursor-pointer"
          />
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800/60">
          <Button
            variant="outline"
            onClick={onClose}
            type="button"
            className="rounded-lg border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={uploading}
            className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {uploading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…</>
            ) : (
              <><Upload className="mr-2 h-4 w-4" /> {submitLabel}</>
            )}
          </Button>
        </div>
      </form>
    </ScrollArea>
  );
}

export default PersonalBookUploadForm;
